import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Share2, Loader2, ImageDown } from "lucide-react";
import { api, formatNumero, formatData, setMeta, cardImageUrl, shareCard, shareLink } from "@/lib/museo";
import { PromiseModal } from "@/components/PromiseModal";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";

export default function PublicPromise() {
  const { numero } = useParams();
  const [promise, setPromise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/promises/${numero}`)
      .then((r) => {
        setPromise(r.data);
        const pageUrl = `${window.location.origin}/promessa/${String(r.data.numero).padStart(6, "0")}`;
        const desc = `${r.data.firma ? `${r.data.firma}: ` : ""}\u201c${r.data.testo}\u201d`;
        setMeta({
          title: `Messaggio ${r.data.numero_formatted} — Il Museo delle Promesse`,
          description: desc,
          canonical: pageUrl,
          ogUrl: pageUrl,
          ogType: "article",
          ogImage: cardImageUrl(r.data.numero),
        });
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [numero]);

  const handleShareLink = async () => {
    await shareLink({ numero: promise.numero, testo: promise.testo, pageUrl: window.location.href, toast });
  };

  const handleShareCard = async () => {
    await shareCard({ numero: promise.numero, testo: promise.testo, firma: promise.firma, toast });
  };

  if (loading) {
    return (
      <div className="relative z-10 min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-museo-accent animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center gap-6 px-6 text-center">
        <p className="font-serif text-3xl text-museo-text">Messaggio non trovato.</p>
        <Link to="/" data-testid="back-home-link" className="text-museo-accent uppercase text-sm tracking-[0.2em] hover:text-museo-text transition-colors">
          Torna al museo
        </Link>
      </div>
    );
  }

  return (
    <div className="relative z-10 min-h-screen flex flex-col">
      <div className="flex-1 max-w-3xl mx-auto px-6 md:px-12 pt-16 md:pt-24 w-full">
        <Link
          to="/"
          data-testid="back-to-museo"
          className="inline-flex items-center gap-2 text-sm text-museo-muted hover:text-museo-accent transition-colors mb-16 uppercase tracking-[0.15em]"
        >
          <ArrowLeft className="w-4 h-4" /> Torna al museo
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex flex-col items-center text-center gap-10 py-12"
        >
          <span className="font-mono text-sm tracking-[0.3em] text-museo-accent" data-testid="promise-numero">
            Messaggio {promise.numero_formatted}
          </span>
          <p className="font-serif text-3xl md:text-5xl leading-snug text-museo-text" data-testid="promise-testo">
            “{promise.testo}”
          </p>
          <span className="font-serif italic text-lg text-museo-muted" data-testid="promise-firma">
            {promise.firma ? `— ${promise.firma}` : "— Anonimo"}
          </span>
          <span className="text-sm text-museo-muted font-light">{formatData(promise.data)}</span>
        </motion.div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
          <button
            data-testid="share-link-button"
            onClick={handleShareLink}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-transparent border border-museo-border text-museo-text px-8 py-4 text-sm tracking-[0.1em] uppercase hover:border-museo-accent transition-all duration-500 rounded-none md:rounded-sm"
          >
            <Share2 className="w-4 h-4" /> Condividi link
          </button>
          <button
            data-testid="share-card-button"
            onClick={handleShareCard}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-transparent border border-museo-border text-museo-text px-8 py-4 text-sm tracking-[0.1em] uppercase hover:border-museo-accent transition-all duration-500 rounded-none md:rounded-sm"
          >
            <ImageDown className="w-4 h-4" /> Condividi messaggio
          </button>
          <button
            data-testid="leave-promise-button"
            onClick={() => setModalOpen(true)}
            className="w-full sm:w-auto bg-museo-accent text-museo-bg px-8 py-4 text-sm tracking-[0.2em] uppercase font-medium hover:bg-museo-text transition-all duration-500 rounded-none md:rounded-sm"
          >
            Lascia anche tu un messaggio
          </button>
        </div>
      </div>

      <Footer />
      <PromiseModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
